import type { MouseEvent } from 'react';
import { UsersThreeIcon } from '@phosphor-icons/react';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { useSendToSpace } from '@/hooks/useSendToSpace';
import { canSendTransaction } from '@/lib/spaces/sending';
import type { Transaction } from '@/types';

export function SpaceSendAction({ transaction }: { transaction?: Transaction }) {
  const sending = useSendToSpace();
  const { spaces } = useSpaceScope();
  if (!sending || !transaction || !canSendTransaction(transaction)) return null;
  const existing = sending.expenses.filter((expense) => expense.sourceTransactionId === transaction.id);
  const first = spaces.find((space) => space.myRole === 'editor' && !space.archivedAt && existing.some((expense) => expense.spaceId === space.id));
  const label = first ? `${first.name} · ajustar` : 'Enviar para espaço';
  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); sending.open(transaction); };
  return <button type="button" onClick={handleOpen} className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-ring">
    <UsersThreeIcon className="size-3" aria-hidden />{label}
  </button>;
}

export function SpaceTransactionMarker({ transactionId }: { transactionId: string }) {
  const sending = useSendToSpace();
  const { spaces } = useSpaceScope();
  if (!sending) return null;
  const expenses = sending.expenses.filter((expense) => expense.sourceTransactionId === transactionId);
  const labels = expenses.flatMap((expense) => {
    const space = spaces.find((item) => item.id === expense.spaceId);
    const rule = sending.rules.find((item) => item.id === expense.ruleId);
    const ruleName = rule?.type === 'description' ? `sua regra · ${rule.value}` : 'sua regra automática';
    return space ? [`${space.name}${expense.via === 'rule' ? ` · ${ruleName}` : ''}`] : [];
  });
  if (!labels.length) return null;
  return <span className="mt-1 flex items-center gap-1 text-xs text-text-secondary"><UsersThreeIcon className="size-3 shrink-0" aria-hidden /><span className="truncate">{labels.join(' · ')}</span></span>;
}

export function SpaceSendLookupAction({ transactionId, eligible }: { transactionId: string; eligible: boolean }) {
  const sending = useSendToSpace();
  const { spaces } = useSpaceScope();
  if (!sending || !eligible) return null;
  const existing = spaces.find((space) => space.myRole === 'editor' && !space.archivedAt && sending.expenses.some((expense) => expense.sourceTransactionId === transactionId && expense.spaceId === space.id));
  const label = existing ? `${existing.name} · ajustar` : 'Enviar para espaço';
  const handleOpen = () => { void sending.openById(transactionId); };
  return <button type="button" onClick={handleOpen} disabled={sending.resolving} aria-busy={sending.resolving} className="mt-1 inline-flex cursor-pointer items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-text-primary hover:bg-surface-sunken disabled:opacity-60"><UsersThreeIcon className="size-3" aria-hidden />{sending.resolving ? 'Abrindo…' : label}</button>;
}
